import { useEffect } from "react";
import clsx from "clsx";
import type { DealStage, DealSummary } from "@edge/shared";
import { StatusDot } from "../shared/StatusDot";

interface PipelineModalProps {
  deals: DealSummary[];
  onSelectDeal: (dealId: string) => void;
  onClose: () => void;
}

/** Display order for columns — matches the Zoho stage progression. */
const STAGE_ORDER: ReadonlyArray<DealStage> = [
  "Active Deals",
  "Underwriting",
  "Doc Signing",
  "Negotiation/Review",
  "Funding",
];

const STAGE_LABELS: Record<DealStage, string> = {
  "Active Deals": "ACTIVE DEALS",
  Underwriting: "UNDERWRITING",
  "Doc Signing": "DOC SIGNING",
  "Negotiation/Review": "NEGOTIATION / REVIEW",
  Funding: "FUNDING",
};

function formatAmount(amount: number | null): string {
  if (amount == null) return "—";
  if (amount >= 1_000_000) {
    const m = amount / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  return `$${Math.round(amount / 1000)}K`;
}

function modifiedLabel(days: number | null): string {
  if (days === null) return "modified —";
  if (days === 0) return "modified today";
  if (days === 1) return "modified 1d ago";
  return `modified ${days}d ago`;
}

/**
 * Pipeline kanban view. Read-only overlay summoned via the header icon or
 * the `open_pipeline` chat directive. Groups the existing cockpit deals
 * (passed in from App.tsx — no separate fetch) by Zoho stage.
 *
 * Closing: ✕ button, Escape key, or backdrop click. Read-only modal so
 * backdrop-dismiss is allowed (unlike the WriteConfirm boundary).
 *
 * Clicking a deal card → opens that deal in Focus mode AND closes the modal.
 */
export function PipelineModal({
  deals,
  onSelectDeal,
  onClose,
}: PipelineModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Group deals by stage. Input order from /api/deals/strip is
  // Modified_Time DESC, which we preserve within each column.
  const byStage = new Map<DealStage, DealSummary[]>();
  for (const stage of STAGE_ORDER) byStage.set(stage, []);
  for (const d of deals) {
    const list = byStage.get(d.stage);
    if (list) list.push(d);
  }

  return (
    <div
      className="write-confirm-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="pipeline-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pipeline-header">
          <div className="pipeline-title">
            <span className="pipeline-title-marker" />
            PIPELINE
            <span className="pipeline-count">· {deals.length} OPEN DEALS</span>
          </div>
          <button
            className="email-detail-close"
            onClick={onClose}
            title="Close (Esc)"
            aria-label="Close pipeline"
          >
            ✕
          </button>
        </div>

        <div className="pipeline-body">
          {STAGE_ORDER.map((stage) => {
            const list = byStage.get(stage) ?? [];
            return (
              <div className="pipeline-column" key={stage}>
                <div className="pipeline-column-header">
                  <span className="pipeline-column-label">
                    {STAGE_LABELS[stage]}
                  </span>
                  <span className="pipeline-column-count">· {list.length}</span>
                </div>
                <div className="pipeline-column-list">
                  {list.length === 0 ? (
                    <div className="pipeline-empty">(no deals)</div>
                  ) : (
                    list.map((d) => (
                      <PipelineCard
                        key={d.id}
                        deal={d}
                        onSelect={onSelectDeal}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PipelineCard({
  deal,
  onSelect,
}: {
  deal: DealSummary;
  onSelect: (dealId: string) => void;
}) {
  return (
    <div
      className={clsx("pipeline-card", deal.urgent && "urgent")}
      onClick={() => onSelect(deal.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(deal.id);
        }
      }}
    >
      <div className="pipeline-card-name">{deal.name}</div>
      <div className="pipeline-card-meta">
        <span className="pipeline-card-amount">{formatAmount(deal.amount)}</span>
        <span className="pipeline-card-close">{deal.closeDate ?? "Close TBD"}</span>
      </div>
      <div className="pipeline-card-dots">
        <StatusDot info={deal.dots.watch} label="WTCH" />
        <StatusDot info={deal.dots.tsk} label="TSK" />
      </div>
      <div className="pipeline-card-age">
        {modifiedLabel(deal.daysSinceModified)}
      </div>
    </div>
  );
}
